<!-- START: Intro -->
<div class="card mt-6">
    <div class="card-body d-flex flex-wrap align-items-center gap-4 py-4">
        <i class="fas fa-shield-halved fs-1 text-primary"></i>
        <div class="flex-grow-1 intro-text">
            <span class="fs-5 fw-bold text-dark me-2"> {{ 'HEDGING_INTRO_TITLE' | translate }} </span>
            <span class="fs-7 text-muted"> {{ 'HEDGING_INTRO_TEXT' | translate }} </span>
        </div>
        <button class="btn btn-sm btn-light-primary" (click)="showHelp = !showHelp">
            <i class="fas fa-circle-question"></i> {{ 'HOW_IT_WORKS' | translate }}
        </button>
    </div>
    <div class="card-body border-top py-4" *ngIf="showHelp">
        <div class="row g-4">
            <div class="col-12 col-md-6 col-xl-3" *ngFor="let topic of ['LOCKING', 'MARKET', 'COVERAGE', 'WHY_NOT_100']">
                <div class="h-100 rounded p-4 bg-light">
                    <div class="fw-bold fs-6 text-dark mb-1"> {{ 'HELP_' + topic + '_TITLE' | translate }} </div>
                    <div class="fs-7 text-muted"> {{ 'HELP_' + topic + '_TEXT' | translate }} </div>
                </div>
            </div>
        </div>
    </div>
</div>
<!-- END: Intro -->

<!-- START: Step 1 - Market -->
<div class="card mt-4" *ngIf="omipPrices.length > 0">
    <div class="card-header step-header" role="button" (click)="marketStepExpanded = !marketStepExpanded">
        <div class="d-flex align-items-center gap-4">
            <span class="step-number">1</span>
            <div>
                <div class="fs-5 fw-bold text-dark lh-sm"> {{ 'STEP_MARKET_TITLE' | translate }} </div>
                <div class="fs-7 text-muted"> {{ 'STEP_MARKET_SUBTITLE' | translate }} </div>
            </div>
        </div>
        <i class="fas text-muted" [ngClass]="marketStepExpanded ? 'fa-chevron-up' : 'fa-chevron-down'"></i>
    </div>
    <div class="card-body row py-4" *ngIf="marketStepExpanded">
        <div class="col-12 col-lg-3">
            <div class="prices-for-future-listing">
                <ng-container *ngFor="let omipIntervals of omipIntervalPriceData | keyvalue">
                    <div *ngIf="omipIntervals.value as omipPrice" (click)="selectOmipPrice(omipPrice);" [ngClass]="{'selected': selectedOmipPrice?.id == omipPrice.id}" class="rounded border border-1 border-solid py-2 px-3 fs-8 text-muted bg-secondary border-dark-clarity" role="button">
                        <div class="fw-bold text-dark fs-7">
                            {{ getFriendlyPeriodLabel(omipPrice) }}
                        </div>
                        <div class="d-flex align-items-baseline justify-content-between mt-1">
                            <div>
                                <span class="fw-bolder fs-4 text-dark"> {{ omipPrice.lastPrice_MW | number:'1.2-2' }} </span> €/MWh
                            </div>
                            <span *ngIf="omipTrendsById.get(omipPrice.id) as trend" class="badge badge-light text-gray-700 fs-8" [matTooltip]="'MARKET_TREND_TOOLTIP' | translate">
                                <i class="fas me-1 text-gray-700" [ngClass]="{ 'fa-arrow-trend-up': trend.direction == 'up', 'fa-arrow-trend-down': trend.direction == 'down', 'fa-minus': trend.direction == 'stable' }"></i>
                                {{ trend.percentage > 0 ? '+' : '' }}{{ trend.percentage | number:'1.0-1' }}%
                            </span>
                        </div>
                    </div>
                </ng-container>
            </div>
        </div>
        <div class="col-12 col-lg-9 mt-5 mt-lg-0">
            <div class="fs-5 fw-bold text-dark"> {{ 'PRICE_EVOLUTION' | translate }}: {{ selectedOmipPrice ? getFriendlyPeriodLabel(selectedOmipPrice) : '' }} </div>
            <div class="fs-7 text-muted mb-3"> {{ 'MARKET_CHART_CAPTION' | translate }} </div>
            <div class="rounded border border-1 border-solid bg-secondary border-dark-clarity">
                <is-chart
                    [chart]="omipPriceChartOptions.chart"
                    [markers]="omipPriceChartOptions.markers"
                    [xaxis]="omipPriceChartOptions.xaxis"
                    [yaxis]="omipPriceChartOptions.yaxis"
                    [grid]="omipPriceChartOptions.grid"
                    [series]="omipPriceChartOptions.series"
                    [stroke]="omipPriceChartOptions.stroke"
                    [tooltip]="omipPriceChartOptions.tooltip">
                </is-chart>
            </div>
        </div>
    </div>
</div>
<!-- END: Step 1 - Market -->

<div #parent *ngIf="currentEquipments && currentEquipments.length > 0">

    <!-- START: Step 2 - Choose prices -->
    <div class="card mt-4">
        <div class="card-header step-header">
            <div class="d-flex align-items-center gap-4">
                <span class="step-number">2</span>
                <div>
                    <div class="fs-5 fw-bold text-dark lh-sm"> {{ 'STEP_CHOOSE_TITLE' | translate }} </div>
                    <div class="fs-7 text-muted"> {{ 'STEP_CHOOSE_SUBTITLE' | translate }} </div>
                </div>
            </div>
        </div>
        <div class="card-body py-4">
            <div class="row g-5">

                <!-- START: Offers -->
                <div class="col-12 col-xl-4">
                    <div class="fs-5 fw-bold text-dark">
                        {{ 'AVAILABLE_PRICES' | translate }}
                        <i class="fas fa-circle-info text-muted ms-1 fs-7" [matTooltip]="'AVAILABLE_PRICES_TOOLTIP' | translate"></i>
                    </div>
                    <div class="fs-7 text-muted mb-3"> {{ (newDisplayMode == 'Covers' ? 'OFFERS_HINT_DRAG' : 'OFFERS_HINT') | translate }} </div>

                    <div *ngIf="isEconomicConditionsFilterActive" class="rounded bg-light-info text-info fs-8 p-3 mb-3">
                        <i class="fas fa-circle-info text-info me-1"></i> {{ 'SAME_CONDITIONS_HINT' | translate }}
                    </div>

                    <div class="price-period-card">
                        <div *ngIf="currentElecPricesByEconomicConditionFilter.length > 0; else noPricesAvailable" cdkDropList cdkDropListSortingDisabled="true">
                            <div *ngFor="let elecPrice of currentElecPricesByEconomicConditionFilter"
                                cdkDrag [cdkDragDisabled]="newDisplayMode != 'Covers'" [cdkDragData]="elecPrice" [cdkDragPreviewContainer]="parent"
                                (cdkDragMoved)="dragMove($event)" (cdkDragEnded)="dragDrop($event)"
                                class="offer-card mt-2 position-relative rounded border border-1 border-solid py-2 px-3 fs-8 text-muted bg-secondary border-dark-clarity"
                                [class.price-drag]="newDisplayMode == 'Covers'">
                                <div class="d-flex justify-content-between align-items-center gap-2">
                                    <div class="text-truncate">
                                        <span class="fw-bold text-dark fs-7"> {{ getFriendlyPeriodLabel(elecPrice) }} </span>
                                        <span class="fs-8 text-muted ms-1" [title]="elecPrice.description"> {{ elecPrice.description }} </span>
                                    </div>
                                    <i *ngIf="newDisplayMode == 'Covers'" class="fas fa-grip-vertical text-gray-400 fs-6" [matTooltip]="'DRAG_HINT' | translate"></i>
                                </div>
                                <div class="d-flex align-items-center justify-content-between mt-1 gap-2">
                                    <div class="d-flex flex-wrap align-items-center column-gap-2">
                                        <span class="text-nowrap"><span class="fw-bolder fs-4 text-dark"> {{ elecPrice.basePrice | number:'1.2-2' }} </span> €/MWh</span>
                                        <span *ngIf="getMarketComparisonLabel(elecPrice) as comparison" class="badge badge-light text-gray-700 fs-9" [matTooltip]="'MARKET_COMPARISON_TOOLTIP' | translate">
                                            {{ comparison.key | translate: { percentage: (comparison.percentage | number:'1.0-0') } }}
                                        </span>
                                    </div>
                                    <button class="btn btn-sm btn-primary text-nowrap" (click)="openCoverDialog(elecPrice)">
                                        <i class="fas fa-lock fs-8"></i> {{ 'LOCK_IN_PRICE' | translate }}
                                    </button>
                                </div>
                            </div>
                        </div>

                        <ng-template #noPricesAvailable>
                            <span class="text-muted fs-6 fw-semibold">
                                {{ 'NO_ACTIVE_HEDGING_PRICE_AVAILABLE' | translate }}
                            </span>
                        </ng-template>
                    </div>
                </div>
                <!-- END: Offers -->

                <!-- START: Coverage board -->
                <div class="col-12 col-xl-8">
                    <div class="d-flex flex-wrap justify-content-between align-items-center gap-3">
                        <div class="btn-group">
                            <button class="btn btn-sm" [ngClass]="newDisplayMode == 'Covers' ? 'btn-primary' : 'btn-light'" (click)="changeDisplayMode('Covers')">
                                <i class="fas fa-table-cells"></i> {{ 'PROTECTED_PERIODS' | translate }}
                            </button>
                            <button class="btn btn-sm" [ngClass]="newDisplayMode == 'Consumptions' ? 'btn-primary' : 'btn-light'" (click)="changeDisplayMode('Consumptions')">
                                <i class="fas fa-chart-line"></i> {{ 'CONSUMPTION_AND_PRICE_CHART' | translate }}
                            </button>
                        </div>
                        <div class="d-flex flex-wrap align-items-center gap-1">
                            <span class="fs-7 text-muted me-1"> {{ 'YEARS_SHOWN' | translate }}: </span>
                            <button *ngFor="let year of availableYears" class="badge border-0 fs-7" (click)="toggleYear(year)"
                                [class.badge-success]="selectedYears.includes(year)" [class.badge-light]="!selectedYears.includes(year)"
                                [matTooltip]="isYearLocked(year) ? ('YEAR_LOCKED_TOOLTIP' | translate) : ''">
                                {{ year }}
                                <i *ngIf="isYearLocked(year)" class="fas fa-lock fs-9 ms-1 text-white"></i>
                            </button>
                        </div>
                    </div>

                    <div *ngIf="selectedPrices.length == 0" class="rounded border border-dashed border-primary bg-light-primary py-2 px-3 mt-3 d-flex align-items-center gap-3">
                        <i class="fas fa-hand-pointer text-primary fs-4"></i>
                        <div class="fs-7">
                            <span class="fw-bold text-dark me-1"> {{ 'COVER_EMPTY_TITLE' | translate }}. </span>
                            <span class="text-muted"> {{ 'COVER_EMPTY_TEXT' | translate }} </span>
                        </div>
                    </div>

                    <div *ngIf="newDisplayMode == 'Consumptions'" class="mt-3">
                        <div class="fs-7 text-muted mb-2"> {{ 'CONSUMPTION_CHART_CAPTION' | translate }} </div>
                        <is-chart [chart]="simulationChartOptions.chart!" [series]="simulationChartOptions.series!" [stroke]="simulationChartOptions.stroke!"
                            [fill]="simulationChartOptions.fill!" [markers]="simulationChartOptions.markers!" [xaxis]="simulationChartOptions.xaxis!"
                            [yaxis]="simulationChartOptions.yaxis!" [legend]="simulationChartOptions.legend!" [grid]="simulationChartOptions.grid!"
                            [tooltip]="simulationChartOptions.tooltip!" [dataLabels]="simulationChartOptions.dataLabels!" [annotations]="simulationChartOptions.annotations!">
                        </is-chart>
                    </div>

                    <div class="drag-drop-graph" *ngIf="newDisplayMode == 'Covers'" #yearDropContainer>
                        <div class="d-flex flex-wrap gap-5 fs-8 text-muted mt-3">
                            <span><span class="legend-swatch protected"></span> {{ 'LEGEND_PROTECTED' | translate }}</span>
                            <span><span class="legend-swatch market"></span> {{ 'LEGEND_INDEXED' | translate }}</span>
                            <span *ngIf="contractCovers.length > 0"><span class="legend-swatch contract"></span> {{ 'LEGEND_CONTRACT_COVER' | translate }}</span>
                        </div>

                        <div *ngFor="let yearData of consumptionsByDateTypes; trackBy: trackByYear" class="mt-3">
                            <div class="d-flex align-items-baseline gap-2 mb-1">
                                <span class="fs-5 fw-bold text-dark"> {{ yearData.year }} </span>
                                <span class="fs-7 text-muted"> · {{ 'YEAR_PROTECTED_SHARE' | translate: { percentage: (yearData.totalCoverPercentage | number:'1.0-0') } }} </span>
                            </div>
                            <div id="{{yearData.year}}" class="rounded w-100 year-drop-container">
                                <div class="title-container">
                                    <ng-container *ngFor="let quarterData of yearData.quarters">
                                        <div *ngFor="let monthData of quarterData.months" class="month-title bg-light-secondary h-100 text-center fw-bold py-1">
                                            {{monthData.monthAsString ?? monthData.month}}
                                        </div>
                                    </ng-container>
                                </div>
                                <div class="coverage-row">
                                    <div *ngFor="let cover of getCoverByMonth(yearData.year)" class="coverage-cell" [matTooltip]="'MONTH_COVER_TOOLTIP' | translate: { percentage: (cover.total | number:'1.0-0') }">
                                        <div class="coverage-bar d-flex">
                                            <div class="coverage-bar-fill contract" [style.width.%]="cover.contract"></div>
                                            <div class="coverage-bar-fill" [style.width.%]="cover.total - cover.contract"></div>
                                        </div>
                                        <div class="fs-9 text-muted text-center"> {{ cover.total | number:'1.0-0' }}% </div>
                                    </div>
                                </div>
                                <div class="drag-drop-container">
                                    <div class="month-grid-background">
                                        <ng-container *ngFor="let quarterData of yearData.quarters">
                                            <div *ngFor="let monthData of quarterData.months" class="month-drag-drop-container">
                                            </div>
                                        </ng-container>
                                    </div>
                                    <div class="price-period-container">
                                        <ng-container *ngFor="let priceContainer of getSelectedPricesContainerDataByYear(yearData.year); trackBy: trackByPriceContainer">
                                            <!-- Contract covers are read-only: no edit dialog and no remove button -->
                                            <div class="locked-price-block position-relative border border-1 border-solid py-1 ps-2 pe-5 fs-8 lh-sm text-muted" [attr.role]="priceContainer.isContractCover ? null : 'button'"
                                                (click)="!priceContainer.isContractCover && openCoverDialog(priceContainer.price)"
                                                [matTooltip]="(priceContainer.isContractCover ? 'CONTRACT_COVER_TOOLTIP' : 'EDIT_COVER_TOOLTIP') | translate"
                                                [matTooltipDisabled]="hoveredRemovePriceId == priceContainer.price.id"
                                                [ngClass]="priceContainer.getClass(yearData.year)"
                                                [style.gridColumn]="priceContainer.gridColumn"
                                                [style.gridRow]="priceContainer.gridRow">
                                                <i *ngIf="priceContainer.isContractCover" class="fas fa-lock contract-cover-icon"></i>
                                                <div *ngIf="!priceContainer.isContractCover" class="text-muted remove-cover-button" [matTooltip]="'REMOVE' | translate"
                                                    (mouseenter)="hoveredRemovePriceId = priceContainer.price.id" (mouseleave)="hoveredRemovePriceId = null"
                                                    (click)="removePriceCover(priceContainer.price); $event.stopPropagation()">
                                                    <i class="fas fa-times"></i>
                                                </div>
                                                <div>
                                                    <div class="fw-bold text-dark text-truncate">
                                                        {{ getFriendlyPeriodLabel(priceContainer.price) }}
                                                    </div>
                                                    <div class="text-truncate">
                                                        <span class="fw-bolder fs-7 text-dark"> {{ priceContainer.coverPercentage }}% </span><ng-container *ngIf="!priceContainer.isContractCover"> · {{ priceContainer.price.basePrice | number:'1.2-2' }} €/MWh</ng-container>
                                                    </div>
                                                </div>
                                            </div>
                                        </ng-container>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                <!-- END: Coverage board -->
            </div>
        </div>
    </div>
    <!-- END: Step 2 - Choose prices -->

    <!-- START: Step 3 - Result -->
    <div class="card mt-4 mb-10">
        <div class="card-header step-header">
            <div class="d-flex align-items-center gap-4">
                <span class="step-number">3</span>
                <div>
                    <div class="fs-5 fw-bold text-dark lh-sm"> {{ 'STEP_RESULT_TITLE' | translate }} </div>
                    <div class="fs-7 text-muted"> {{ 'STEP_RESULT_SUBTITLE' | translate }} </div>
                </div>
            </div>
        </div>
        <div class="card-body py-5">
            <div class="table-responsive">
                <table class="table align-middle gs-0 gy-3 mb-0 result-table">
                    <thead>
                        <tr class="fs-8 fw-bold text-muted text-uppercase border-bottom">
                            <th> {{ 'YEAR' | translate }} </th>
                            <th class="text-end"> {{ 'ESTIMATED_CONSUMPTION' | translate }} </th>
                            <th class="protected-column"> {{ 'PROTECTED_SHARE' | translate }} </th>
                            <th class="text-end"><i class="fas fa-lock text-primary me-1 fs-8"></i> {{ 'LOCKED_COST' | translate }} </th>
                            <th class="text-end">
                                <i class="fas fa-chart-line text-gray-600 me-1 fs-8"></i> {{ 'INDEXED_COST_ESTIMATE' | translate }}
                                <i class="fas fa-circle-info text-muted ms-1 fs-8" [matTooltip]="'INDEXED_COST_ESTIMATE_TOOLTIP' | translate"></i>
                            </th>
                            <th class="text-end"> {{ 'TOTAL_ESTIMATE' | translate }} </th>
                        </tr>
                    </thead>
                    <tbody class="fs-7">
                        <tr *ngFor="let yearData of consumptionsByDateTypes; trackBy: trackByYear" class="border-bottom border-gray-200">
                            <td class="fs-5 fw-bold text-dark"> {{ yearData.year }} </td>
                            <td class="text-end text-nowrap"> ~{{ yearData.totalConsumption | number:'1.0-3' }} MWh </td>
                            <td>
                                <div class="d-flex align-items-center gap-3">
                                    <!-- The contract's own covers come first, in their own colour -->
                                    <div class="progress h-6px bg-light-secondary flex-grow-1">
                                        <div class="progress-bar contract-cover-bar" role="progressbar" [style.width.%]="yearData.contractCoverPercentage"></div>
                                        <div class="progress-bar bg-primary" role="progressbar" [style.width.%]="yearData.totalCoverPercentage - yearData.contractCoverPercentage"></div>
                                    </div>
                                    <span class="fw-bold text-dark text-nowrap"> {{ yearData.totalCoverPercentage | number:'1.0-0' }}% </span>
                                </div>
                                <div *ngIf="yearData.contractCoverPercentage > 0" class="fs-8 text-gray-600 mt-1">
                                    <span class="legend-swatch contract"></span> {{ 'CONTRACT_COVER_SHARE' | translate: { percentage: (yearData.contractCoverPercentage | number:'1.0-0') } }}
                                </div>
                            </td>
                            <td class="text-end text-nowrap">
                                <div class="fw-bold text-dark">
                                    {{ yearData.coveredCost | number:'1.0-0' }} €
                                    <!-- Each cover's share of the year (its MWh × its price over this year's months) is listed in the tooltip -->
                                    <i *ngIf="yearData.lockedParts.length > 0" class="fas ms-1 fs-8"
                                        [ngClass]="yearData.hasCappedLockedPart ? 'fa-triangle-exclamation text-warning' : 'fa-circle-info text-muted'"
                                        matTooltipClass="breakdown-tooltip"
                                        [matTooltip]="getLockedCostBreakdownTooltip(yearData)"></i>
                                </div>
                            </td>
                            <td class="text-end text-nowrap">
                                <div class="fw-bold text-dark">
                                    {{ (yearData.uncoveredConsumption > 0 && yearData.uncoveredCostEstimate == 0) ? '—' : (yearData.uncoveredCostEstimate | number:'1.0-0') + ' €' }}
                                </div>
                                <div class="text-muted fs-8" *ngIf="yearData.uncoveredConsumption > 0">
                                    <ng-container *ngIf="yearData.averageIndexedPrice > 0; else noIndexedPrice">
                                        {{ yearData.uncoveredConsumption | number:'1.0-3' }} MWh × {{ yearData.averageIndexedPrice | number:'1.2-2' }} €/MWh
                                    </ng-container>
                                    <ng-template #noIndexedPrice>
                                        {{ yearData.uncoveredConsumption | number:'1.0-3' }} MWh {{ 'AT_INDEXED_PRICE' | translate }}
                                    </ng-template>
                                </div>
                            </td>
                            <td class="text-end text-nowrap">
                                <div class="fs-5 fw-bolder text-primary"> {{ yearData.totalCostEstimate | number:'1.0-0' }} € </div>
                                <div class="text-muted fs-8"> {{ 'AVERAGE_PRICE_PER_MWH' | translate: { price: (yearData.averagePriceEstimate | number:'1.2-2') } }} </div>
                            </td>
                        </tr>
                    </tbody>
                </table>
            </div>

            <!-- START: Indexed price formula -->
            <div *ngIf="yearsWithIndexedConsumption.length > 0" class="mt-4">
                <button class="btn btn-sm btn-light" (click)="openIndexedPriceFormulaInfo()">
                    <i class="fas fa-calculator text-gray-600"></i> {{ 'INDEXED_PRICE_FORMULA_TITLE' | translate }}
                </button>
            </div>
            <!-- END: Indexed price formula -->

            <div class="d-flex flex-wrap justify-content-between align-items-center gap-4 mt-4">
                <div class="fs-8 text-muted flex-grow-1 result-footnote">
                    <i class="fas fa-circle-info me-1"></i> {{ 'RESULT_FOOTNOTE' | translate }}
                </div>
                <div class="d-flex align-items-center gap-4">
                    <span class="text-muted fs-7" *ngIf="selectedPrices.length == 0"> {{ 'SAVE_DISABLED_HINT' | translate }} </span>
                    <button class="btn btn-primary" [disabled]="selectedPrices.length == 0" (click)="showCoversSummaryDataDialog()">
                        {{ 'SAVE_AND_CREATE_PROPOSAL' | translate }}
                    </button>
                </div>
            </div>
        </div>
    </div>
    <!-- END: Step 3 - Result -->
</div>
